<!-- src/components/CommitmentModal.vue -->
<template>
  <div class="modal fade" id="setCommitmentModal" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ form.id ? "Edit Commitment (Cycle)" : "Set Commitment (Cycle)" }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <div v-if="formError" class="alert alert-danger py-2 mb-3">{{ formError }}</div>

          <div class="row g-2">
            <!-- Member -->
            <div class="col-md-6">
              <label class="form-label">Member</label>

              <!-- ✅ Read-only in bulk mode -->
              <div v-if="lockMember" class="border rounded p-2 bg-light">
                <div class="fw-semibold">{{ selectedMember?.name || "—" }}</div>
                <div class="text-muted small">
                  {{ selectedMember?.phone || selectedMember?.email || "—" }}
                </div>
                <div class="text-muted small mt-1">Selected from bulk table</div>
              </div>

              <!-- Dropdown mode (commitments page) -->
              <select
                v-else
                v-model="form.user_id"
                class="form-select"
                :disabled="!!form.id"
              >
                <option value="">Select member</option>
                <option v-for="m in members" :key="m.id" :value="m.id">
                  {{ m.name }} ({{ m.phone || "-" }})
                </option>
              </select>

              <div v-if="!lockMember && form.id" class="text-muted small mt-1">
                Member cannot be changed on edit.
              </div>
            </div>

            <!-- Amount -->
            <div class="col-md-6">
              <label class="form-label">Amount per month</label>
              <input
                v-model.number="form.amount"
                type="number"
                class="form-control"
                min="0"
                placeholder="0"
              />
              <div class="text-muted small mt-1">
                Must be ≥ system minimum. This amount applies to the whole cycle.
              </div>
            </div>

            <!-- Start period (modern month picker) -->
            <div class="col-md-6">
              <label class="form-label">
                Start period <span class="text-muted small">(cycle start month)</span>
              </label>

              <input
                ref="startPeriodInput"
                type="text"
                class="form-control"
                :disabled="!!form.id"
                placeholder="Pick month..."
              />

              <!-- keep model visible for debugging if needed -->
              <div class="text-muted small mt-1">
                Selected: <strong>{{ form.start_period || "—" }}</strong>
              </div>
            </div>

            <!-- Notes -->
            <div class="col-md-6">
              <label class="form-label">Notes</label>
              <input
                v-model="form.note"
                type="text"
                class="form-control"
                placeholder="Optional (frontend only)"
              />
            </div>
          </div>

          <div class="alert alert-warning mt-3 mb-0">
            <div class="fw-semibold mb-1">Important</div>
            <div class="small">
              Commitments should be changed only at cycle boundaries (backend enforces).
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-outline-secondary" data-bs-dismiss="modal">
            Cancel
          </button>
          <button class="btn btn-primary" :disabled="saving" @click="submit">
            {{ saving ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/services/http";
import { Modal } from "bootstrap";

import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";

// Month select plugin (installed with flatpickr)
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import "flatpickr/dist/plugins/monthSelect/style.css";

export default {
  name: "CommitmentModal",
  props: {
    members: { type: Array, default: () => [] },
  },

  data() {
    return {
      modal: null,
      fp: null,

      saving: false,
      formError: "",

      // bulk-mode UX
      lockMember: false,
      selectedMember: null,

      form: {
        id: null,        // frontend helper only
        user_id: "",
        amount: "",
        start_period: "", // "YYYY-MM"
        note: "",
      },
    };
  },

  methods: {
    monthKeyToday() {
      const t = new Date();
      const yyyy = t.getFullYear();
      const mm = String(t.getMonth() + 1).padStart(2, "0");
      return `${yyyy}-${mm}`;
    },

    destroyPicker() {
      if (this.fp) {
        this.fp.destroy();
        this.fp = null;
      }
    },

    initMonthPicker() {
      this.destroyPicker();

      const el = this.$refs.startPeriodInput;
      if (!el) return;

      const initial = this.form.start_period || this.monthKeyToday();

      this.fp = flatpickr(el, {
        plugins: [
          monthSelectPlugin({
            shorthand: true,
            dateFormat: "Y-m", // model format -> "YYYY-MM"
            altFormat: "F Y",  // UI format -> "January 2026"
            theme: "light",
          }),
        ],
        defaultDate: initial,
        disableMobile: true, // ensures consistent picker on mobile too
        onReady: (_, dateStr) => {
          // ensure model is synced even if user doesn't change it
          this.form.start_period = dateStr || initial;
        },
        onChange: (_, dateStr) => {
          this.form.start_period = dateStr; // "YYYY-MM"
        },
      });

      // Guarantee the input shows the current model
      this.fp.setDate(initial, true, "Y-m");
    },

    // ✅ Called from bulk page: this.$refs.commitmentModal.openForMember(r.user, period)
    openForMember(member, startPeriod = "") {
      this.formError = "";
      this.lockMember = true;
      this.selectedMember = member || null;

      this.form = {
        id: null,
        user_id: member?.id || "",
        amount: "",
        start_period: startPeriod || this.monthKeyToday(),
        note: "",
      };

      this.modal.show();
      this.$nextTick(() => this.initMonthPicker());
    },

    // ✅ Called from commitments page: set blank
    openBlank(startPeriod = "") {
      this.formError = "";
      this.lockMember = false;
      this.selectedMember = null;

      this.form = {
        id: null,
        user_id: "",
        amount: "",
        start_period: startPeriod || this.monthKeyToday(),
        note: "",
      };

      this.modal.show();
      this.$nextTick(() => this.initMonthPicker());
    },

    // ✅ Optional: called from commitments page edit button
    openEdit(row) {
      this.formError = "";
      this.lockMember = false;
      this.selectedMember = null;

      this.form = {
        id: row?.id || null,
        user_id: row?.user_id || "",
        amount: Number(row?.amount || 0),
        start_period: row?.cycle_start_period || this.monthKeyToday(),
        note: "",
      };

      this.modal.show();
      this.$nextTick(() => this.initMonthPicker());
    },

    async submit() {
      this.saving = true;
      this.formError = "";

      try {
        // basic frontend guard
        if (!this.form.user_id) throw new Error("Please select a member.");
        if (this.form.amount === "" || this.form.amount === null) throw new Error("Please enter an amount.");
        if (!this.form.start_period) throw new Error("Please pick a start period.");

        const payload = {
          user_id: this.form.user_id,
          amount: this.form.amount,
          start_period: this.form.start_period, // "YYYY-MM"
        };

        await http.post("/commitments", payload);

        this.modal.hide();
        this.$emit("saved", { user_id: payload.user_id, start_period: payload.start_period });
      } catch (e) {
        // Local thrown error
        if (e instanceof Error && !e?.response) {
          this.formError = e.message;
          this.saving = false;
          return;
        }

        // Laravel validation (message + errors)
        const msg = e?.response?.data?.message;
        const errs = e?.response?.data?.errors;

        if (errs) {
          const firstKey = Object.keys(errs)[0];
          this.formError = errs[firstKey]?.[0] || msg || "Failed to save commitment.";
        } else {
          this.formError = msg || "Failed to save commitment.";
        }
      } finally {
        this.saving = false;
      }
    },
  },

  mounted() {
    this.modal = new Modal(document.getElementById("setCommitmentModal"));

    // Cleanup picker on close (prevents duplicate instances)
    document.getElementById("setCommitmentModal")?.addEventListener("hidden.bs.modal", () => {
      this.destroyPicker();
      this.formError = "";
      this.saving = false;
    });
  },

  beforeUnmount() {
    this.destroyPicker();
  },
};
</script>

<style scoped>
/* Small improvement: make flatpickr input feel consistent with Bootstrap */
:deep(.flatpickr-input[readonly]) {
  background-color: #fff;
}
</style>
